import { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { trackEvent } from '../lib/analytics'
import { usePageMeta } from '../hooks/usePageMeta'
import { useSavedDecks } from '../hooks/useSavedDecks'
import { useCollection } from '../hooks/useCollection'
import { useAuth } from '../hooks/useAuth'
import { useCardImageIndex } from '../hooks/useCardImages'
import { useCollectionScope } from '../hooks/useCollectionScope'
import DeckEntry, { type DeckEntryOrigin } from '../components/input/DeckEntry'
import type { FetchedDeckLink } from '../lib/deckSync'
import RecentDeckTile from '../components/home/RecentDeckTile'
import CollectionStrip from '../components/home/CollectionStrip'
import ProBand from '../components/home/ProBand'
import PopularCommandersRow from '../components/home/PopularCommandersRow'
import BlogRow from '../components/home/BlogRow'
import BracketPrimer from '../components/input/BracketPrimer'
import PageShell from '../components/layout/PageShell'
import { sentenceSeparator, useCommonT } from '../i18n'
import { navV2Enabled } from '../lib/navV2'
import PhoneHomeV2 from '../components/home/PhoneHomeV2'

export default function HomePage() {
  // Page meta stays English, like every route's ROUTE_META (SEO is out of
  // scope for localization).
  usePageMeta(
    // eslint-disable-next-line i18next/no-literal-string -- page meta stays English (SEO), as ROUTE_META does
    'CommanderBracket - Scan it. Sort it. Build it. Bracket.',
    // eslint-disable-next-line i18next/no-literal-string -- page meta stays English (SEO), as ROUTE_META does
    'Free Commander toolkit for Magic: The Gathering. Build a deck, ask the judge, check your bracket. No login required.',
  )
  const t = useCommonT()
  // Funnel: top of the funnel for paid traffic. Fires on every visit
  // regardless of state so landing -> analyze_started conversion stays
  // measurable (and the total must never drop with this redesign).
  useEffect(() => {
    trackEvent('landing_view')
  }, [])

  const { decks, loading: decksLoading } = useSavedDecks()
  const { collectionId, cardCount, ownedCards } = useCollection()
  // Navigation v2 adds the phone Home blocks (PhoneHomeV2, md:hidden) and
  // hides the two bands they replace on phones. Desktop is unchanged.
  const navV2 = navV2Enabled()
  // eslint-disable-next-line i18next/no-literal-string -- CSS classes
  const phoneHidden = navV2 ? 'hidden md:block' : ''
  const { loading: authLoading } = useAuth()
  const navigate = useNavigate()
  // Home does NOT analyze. It routes the decklist into the one analyzer
  // (BracketPage), which reads location.state and runs its normal intake:
  // marked lists analyze, unmarked ones are seeded behind the commander gate
  // WITH their link and origin, so a URL pasted here saves linked exactly
  // like one pasted on /bracket, and a SlingLab fetch is never read as a paste.
  const routeToBracket = (decklist: string, link: FetchedDeckLink | undefined, origin: DeckEntryOrigin) => {
    navigate('/bracket', { state: { decklist, source: 'home', link, origin } })
  }
  const pickPopular = (commander: string) => {
    trackEvent('home_popular_clicked', { commander })
    navigate(`/build?commander=${encodeURIComponent(commander)}`)
  }

  // Flash guard (Codex): `decks` inits [] / loading:true and `useCollection`
  // hydrates from localStorage before cloud auth, so render the new-visitor
  // state (the default) until BOTH auth and saved decks resolve. Only then flip
  // to returning. New-visitor is the loading fallback, which also protects the
  // SEO landing and cold-share recipients. We never flip returning -> new.
  const resolved = !authLoading && !decksLoading
  const isReturning = resolved && (decks.length > 0 || collectionId != null)

  // Most-recent-first, for the "Continue building" CTA + the rail.
  const recentDecks = useMemo(
    () => [...decks].sort((a, b) => b.savedAt.localeCompare(a.savedAt)).slice(0, 4),
    [decks],
  )
  const mostRecent = recentDecks[0]
  // One batched /cards/images call for the (<=4) recent-deck commanders + partners.
  const scope = useCollectionScope()
  const { getInfo: getRecentInfo } = useCardImageIndex(
    recentDecks.flatMap(d => [d.commander, d.partner]).filter((n): n is string => !!n),
    scope,
  )
  // Returning affordances funnel into the builder for that commander - the
  // hero/revenue path - rather than the bracket analyzer.
  const continueTo = mostRecent?.commander
    // eslint-disable-next-line i18next/no-literal-string -- app route
    ? `/build?commander=${encodeURIComponent(mostRecent.commander)}`
    : '/build'

  return (
    <div className={`flex-1 flex flex-col items-center px-4 sm:py-16 ${navV2 ? 'pt-4 pb-12' : 'py-12'}`}>
      <PageShell width="app" padY="none" padX={false} className="space-y-10">
        {/* Hero and the returning "Continue building" CTA (or the new-visitor
            paste box) stay at read width, centered - the funnel-protected
            column. Everything else below runs the full app width. */}
        <div className="max-w-read mx-auto space-y-8 text-center">
          {/* Hero - the brand architecture, shared across both states. Mark
              and headline are one horizontal lockup, matching the iOS home
              screen; the tagline runs two beats per line beside the mark.
              src/prerender/HomeStatic.tsx mirrors this. */}
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-4 sm:gap-5">
              <img
                src="/logo-badge.png"
                alt={t('home.logo_alt')}
                className="h-20 sm:h-24 w-auto shrink-0"
              />
              <h1 className="text-left text-[1.75rem] sm:text-[2.75rem] font-black text-white tracking-tight leading-[1.08]">
                <span className="block">{t('home.brand.scan')}{sentenceSeparator()}{t('home.brand.sort')}</span>{sentenceSeparator()}
                <span className="block">
                  {t('home.brand.build')}{sentenceSeparator()}
                  <span className="bg-gradient-to-r from-green-400 via-purple-400 to-red-400 bg-clip-text text-transparent">
                    {t('home.brand.bracket')}
                  </span>
                </span>
              </h1>
            </div>
            <p className="text-sm font-medium text-gray-300 tracking-wide">
              {t('home.tagline')}
            </p>
          </div>

          {/* Navigation v2's phone Home blocks, right under the hero
              (md:hidden inside PhoneHomeV2; desktop is unchanged). */}
          {navV2 && (
            <PhoneHomeV2
              hasCollection={collectionId != null || (ownedCards?.length ?? 0) > 0}
              cardCount={cardCount}
              recentDecks={resolved ? recentDecks : []}
              getImage={getRecentInfo}
            />
          )}

          {isReturning ? (
            /* RETURNING builder - Build-first fast lane */
            mostRecent && (
              <Link
                to={continueTo}
                onClick={() => trackEvent('home_continue_build', { commander: mostRecent.commander || 'unknown' })}
                className="flex items-center justify-center gap-2 w-full py-4 rounded-xl font-semibold text-base bg-purple-600 text-white hover:bg-purple-500 transition-colors shadow-lg shadow-purple-900/30"
              >
                {mostRecent.commander
                  ? t('home.continue', { name: mostRecent.commander.split(',')[0] })
                  : t('home.continue_generic')}
                <span aria-hidden>&rarr;</span>
              </Link>
            )
          ) : (
            /* NEW visitor - the inline bracket hook is the hero */
            <div className="space-y-6">
              {/* Lean on purpose: the hero field, not the analyzer. The
                  helper sentence and the file row live on /bracket. */}
              <DeckEntry variant="lean" onDecklist={routeToBracket} onEvent={trackEvent} />
              <BracketPrimer />
            </div>
          )}
        </div>

        {isReturning && (
          /* Returning hub bands - full app width, left-aligned. */
          <div className="space-y-8 text-left">
            {recentDecks.length > 0 && (
              <div className={`space-y-2 ${phoneHidden}`}>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t('home.recent')}</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {recentDecks.map((deck) => (
                    <RecentDeckTile key={deck.id} deck={deck} image={deck.commander ? getRecentInfo(deck.commander) : undefined} partnerImage={deck.partner ? getRecentInfo(deck.partner) : undefined} />
                  ))}
                </div>
              </div>
            )}

            {collectionId != null && (
              <div className={phoneHidden}>
                <CollectionStrip cardCount={cardCount} />
              </div>
            )}

            <ProBand />

            {/* Still one analyzer: the same entry, compact until it is used. */}
            <section className="space-y-2 text-left">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t('home.check_deck')}</h2>
              <DeckEntry variant="compact" showExample={false} onDecklist={routeToBracket} onEvent={trackEvent} />
            </section>

            <PopularCommandersRow onPick={pickPopular} />

            {/* One line, deliberately: the precon catalogue is a browse
                surface, not a band, and it competes with nothing above it. */}
            <Link
              to="/explore?tab=precons"
              className="inline-block text-sm text-purple-400 transition-colors hover:text-purple-300"
            >
              {t('home.browse_precons')}
            </Link>
          </div>
        )}

        <BlogRow />
      </PageShell>
    </div>
  )
}
